import React, { useState, useContext } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  useTheme,
  alpha,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSelector } from 'react-redux';
import { RootStateType } from '../../../store';
import CreateShipmentContext from '../../../context/CreateShipment';
import { createShipmentContextType, BulkShipmentType } from '../../../types/shipment';
import SbButton from '../../../components/Button';
import EditBulkShipmentModal from '../UserDetails/EditBulkShipmentModal';
import { validateShipmentFields, getPendingFields, formatDecimal } from './utils/validation';

export default function BatchReviewStep(): JSX.Element {
  const theme = useTheme();
  const { countryList, currencyList } = useSelector((state: RootStateType) => state?.global);

  const { bulkShipments, setBulkShipments, batchCounts, setActiveStep, resetBatchMode } = useContext(
    CreateShipmentContext,
  ) as createShipmentContextType;

  const [editingShipmentIndex, setEditingShipmentIndex] = useState<number | null>(null);

  const totalShipments = bulkShipments.length;
  const readyShipments = bulkShipments.filter((s: BulkShipmentType) => {
    const val = validateShipmentFields(s);
    return (
      s.status !== 'pending' &&
      s.status !== 'processing' &&
      s.status !== 'failed' &&
      s.status !== 'error' &&
      val.isValid
    );
  }).length;
  const needsAttentionShipments = totalShipments - readyShipments;
  const allShipmentsValid = totalShipments > 0 && readyShipments === totalShipments;

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      {/* Hero Header & Quick Summary Banner */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: '16px',
          border: '1px solid',
          borderColor: alpha(theme.palette.primary.main, 0.15),
          background:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.primary.dark, 0.15)
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(
                  theme.palette.primary.light || theme.palette.primary.main,
                  0.08,
                )} 100%)`,
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <Chip
                icon={
                  <AutoAwesomeIcon
                    sx={{ fontSize: '15px !important', color: `${theme.palette.primary.main} !important` }}
                  />
                }
                label="AI Multi-Invoice Extraction"
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.3px',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  borderRadius: '6px',
                  height: '24px',
                }}
              />
              {batchCounts.pending > 0 && (
                <Chip
                  label="Extracting..."
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: '24px', fontSize: '0.75rem', fontWeight: 600 }}
                />
              )}
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 0.5 }}>
              Review Extracted Shipments
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Speedbox AI has parsed your uploaded documents. Verify details below and resolve missing fields before
              selecting logistics and services.
            </Typography>
          </Grid>

          {/* Quick KPI Metric Badges */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                flexWrap: 'wrap',
              }}
            >
              {/* Total Card */}
              <Box
                sx={{
                  px: 2,
                  py: 1.2,
                  borderRadius: '12px',
                  bgcolor: alpha(theme.palette.text.primary, 0.04),
                  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                  minWidth: '95px',
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.6875rem',
                  }}
                >
                  Total Invoices
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2 }}>
                  {totalShipments}
                </Typography>
              </Box>

              {/* Ready Card */}
              <Box
                sx={{
                  px: 2,
                  py: 1.2,
                  borderRadius: '12px',
                  bgcolor: alpha(theme.palette.success.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
                  minWidth: '95px',
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.success.main,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.6875rem',
                  }}
                >
                  Ready
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.success.main, lineHeight: 1.2 }}>
                  {readyShipments}
                </Typography>
              </Box>

              {/* Needs Review Card */}
              <Box
                sx={{
                  px: 2,
                  py: 1.2,
                  borderRadius: '12px',
                  bgcolor:
                    needsAttentionShipments > 0
                      ? alpha(theme.palette.warning.main, 0.08)
                      : alpha(theme.palette.text.primary, 0.04),
                  border: `1px solid ${
                    needsAttentionShipments > 0
                      ? alpha(theme.palette.warning.main, 0.3)
                      : alpha(theme.palette.divider, 0.12)
                  }`,
                  minWidth: '95px',
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: needsAttentionShipments > 0 ? theme.palette.warning.main : theme.palette.text.secondary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.6875rem',
                  }}
                >
                  Needs Action
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: needsAttentionShipments > 0 ? theme.palette.warning.main : theme.palette.text.secondary,
                    lineHeight: 1.2,
                  }}
                >
                  {needsAttentionShipments}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Live Polling Banner */}
      {batchCounts.pending > 0 && (
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            mb: 3,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            borderRadius: '12px',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          }}
        >
          <CircularProgress size={22} sx={{ color: theme.palette.primary.main, mr: 2 }} />
          <Box>
            <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
              AI Extraction in Progress... ({batchCounts.completed} completed, {batchCounts.pending} remaining)
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              Extracted shipments appear in real time. Completed rows are immediately reviewable and editable.
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Shipments Table Container */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
          borderRadius: '12px',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          overflowX: 'auto',
        }}
      >
        <Table sx={{ minWidth: 700 }}>
          <TableHead sx={{ bgcolor: 'var(--mui-palette-primary-main)' }}>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 700,
                  color: 'var(--mui-palette-primary-contrastText)',
                  fontSize: '0.875rem',
                  py: 1.8,
                  letterSpacing: '0.3px',
                }}
              >
                Invoice / Shipment
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  color: 'var(--mui-palette-primary-contrastText)',
                  fontSize: '0.875rem',
                  py: 1.8,
                  letterSpacing: '0.3px',
                }}
              >
                Shipper
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  color: 'var(--mui-palette-primary-contrastText)',
                  fontSize: '0.875rem',
                  py: 1.8,
                  letterSpacing: '0.3px',
                }}
              >
                Consignee
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  color: 'var(--mui-palette-primary-contrastText)',
                  fontSize: '0.875rem',
                  py: 1.8,
                  letterSpacing: '0.3px',
                }}
              >
                Product / Boxes
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 700,
                  color: 'var(--mui-palette-primary-contrastText)',
                  fontSize: '0.875rem',
                  py: 1.8,
                  letterSpacing: '0.3px',
                }}
              >
                Qty
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 700,
                  color: 'var(--mui-palette-primary-contrastText)',
                  fontSize: '0.875rem',
                  py: 1.8,
                  letterSpacing: '0.3px',
                }}
              >
                Weight
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 700,
                  color: 'var(--mui-palette-primary-contrastText)',
                  fontSize: '0.875rem',
                  py: 1.8,
                  letterSpacing: '0.3px',
                }}
              >
                Total Value
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  color: 'var(--mui-palette-primary-contrastText)',
                  fontSize: '0.875rem',
                  py: 1.8,
                  letterSpacing: '0.3px',
                }}
              >
                Status
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: 700,
                  color: 'var(--mui-palette-primary-contrastText)',
                  fontSize: '0.875rem',
                  py: 1.8,
                  letterSpacing: '0.3px',
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bulkShipments.map((shipment: any, index: number) => {
              const valErrors = validateShipmentFields(shipment);
              const invoice = shipment.package?.invoiceNumber || `Shipment ${index + 1}`;
              const isFailed = shipment.status === 'failed' || shipment.status === 'error';
              const isPending = shipment.status === 'pending' || shipment.status === 'processing';
              const isNeedsReview = !valErrors.isValid;

              const firstBox = shipment.package?.box?.[0];
              const firstProd = firstBox?.products?.[0];
              const productDesc = firstProd?.description || '—';
              const qty =
                firstProd?.quantity !== undefined && firstProd?.quantity !== null
                  ? formatDecimal(firstProd.quantity)
                  : '—';
              const rawWeight = firstBox?.weight;
              const weight =
                rawWeight !== undefined && rawWeight !== null && rawWeight !== ''
                  ? `${formatDecimal(rawWeight)} ${shipment.package?.unitOfMeasurement?.split('-')?.[0] || 'kg'}`
                  : '—';
              const calculatedVal = firstBox?.products?.reduce(
                (sum: number, prod: any) => sum + (Number(prod.quantity) || 0) * (Number(prod.unitValue) || 0),
                0,
              );
              const totalVal = calculatedVal !== undefined && calculatedVal !== null ? calculatedVal.toFixed(2) : '—';

              const rowAccentColor = isNeedsReview
                ? '#f57c00'
                : isFailed
                ? '#d32f2f'
                : isPending
                ? '#7c4dff'
                : '#2e7d32';

              return (
                <TableRow
                  key={shipment.jobId || index}
                  hover
                  sx={{
                    borderLeft: `4px solid ${rowAccentColor}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                    },
                  }}
                >
                  {/* Invoice / Shipment Cell */}
                  <TableCell component="th" scope="row" sx={{ py: 2 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.925rem', color: theme.palette.text.primary }}>
                      {invoice}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                      <Chip
                        label={`#${index + 1}`}
                        size="small"
                        sx={{
                          fontSize: '0.6875rem',
                          height: '20px',
                          fontWeight: 700,
                          bgcolor: alpha(theme.palette.text.primary, 0.06),
                          color: theme.palette.text.secondary,
                          borderRadius: '4px',
                        }}
                      />
                      {shipment.package?.userReferenceNumber && (
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>
                          Ref: {shipment.package.userReferenceNumber}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>

                  {/* Shipper Cell */}
                  <TableCell sx={{ py: 2 }}>
                    {shipment.shipper?.name ? (
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: theme.palette.text.primary }}>
                          {shipment.shipper.name}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary, mt: 0.25 }}>
                          {[shipment.shipper.city, shipment.shipper.country, shipment.shipper.pincode]
                            .filter(Boolean)
                            .join(', ')}
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          px: 1,
                          py: 0.5,
                          borderRadius: '4px',
                          bgcolor: alpha(theme.palette.warning.main, 0.08),
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.8125rem',
                            color: theme.palette.warning.dark || '#b78103',
                            fontWeight: 500,
                          }}
                        >
                          Incomplete Shipper
                        </Typography>
                      </Box>
                    )}
                  </TableCell>

                  {/* Consignee Cell */}
                  <TableCell sx={{ py: 2 }}>
                    {shipment.consignee?.name ? (
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: theme.palette.text.primary }}>
                          {shipment.consignee.name}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary, mt: 0.25 }}>
                          {[shipment.consignee.city, shipment.consignee.country, shipment.consignee.pincode]
                            .filter(Boolean)
                            .join(', ')}
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          px: 1,
                          py: 0.5,
                          borderRadius: '4px',
                          bgcolor: alpha(theme.palette.warning.main, 0.08),
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.8125rem',
                            color: theme.palette.warning.dark || '#b78103',
                            fontWeight: 500,
                          }}
                        >
                          Incomplete Consignee
                        </Typography>
                      </Box>
                    )}
                  </TableCell>

                  {/* Product Cell */}
                  <TableCell sx={{ maxWidth: 220, py: 2 }}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        color: theme.palette.text.primary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {productDesc}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.75rem',
                        display: 'block',
                        mt: 0.25,
                      }}
                    >
                      {shipment.package?.box?.length || 1} Box(es) · {shipment.package?.box?.[0]?.products?.length || 1}{' '}
                      Item(s)
                    </Typography>
                  </TableCell>

                  {/* Qty Cell */}
                  <TableCell align="right" sx={{ py: 2 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: theme.palette.text.primary }}>
                      {qty}
                    </Typography>
                  </TableCell>

                  {/* Weight Cell */}
                  <TableCell align="right" sx={{ py: 2 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: theme.palette.text.primary }}>
                      {weight}
                    </Typography>
                  </TableCell>

                  {/* Value Cell */}
                  <TableCell align="right" sx={{ py: 2 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        color: theme.palette.text.primary,
                      }}
                    >
                      {totalVal !== '—' && shipment.package?.currency ? `${shipment.package.currency} ` : ''}
                      {totalVal}
                    </Typography>
                  </TableCell>

                  {/* Status Cell */}
                  <TableCell sx={{ verticalAlign: 'middle', py: 2 }}>
                    {isPending ? (
                      <Chip
                        label="Processing"
                        variant="outlined"
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    ) : isFailed ? (
                      <Box>
                        <Chip
                          icon={<ErrorIcon />}
                          label="Failed"
                          color="error"
                          variant="outlined"
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                        {shipment.error && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.error.main,
                              display: 'block',
                              mt: 0.5,
                              fontSize: '0.725rem',
                            }}
                          >
                            {typeof shipment.error === 'string' ? shipment.error : JSON.stringify(shipment.error)}
                          </Typography>
                        )}
                      </Box>
                    ) : isNeedsReview ? (
                      <Box>
                        <Chip
                          icon={<WarningIcon />}
                          label="Needs Attention"
                          color="warning"
                          variant="outlined"
                          size="small"
                          sx={{ fontWeight: 700, mb: 0.75 }}
                        />
                        {shipment.reviewReason && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.warning.dark || '#b78103',
                              display: 'block',
                              mb: 0.75,
                              fontSize: '0.725rem',
                              fontWeight: 500,
                            }}
                          >
                            ⚠️ {shipment.reviewReason}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {getPendingFields(shipment).map(field => (
                              <Chip
                                key={field}
                                label={field}
                                size="small"
                                sx={{
                                  fontSize: '0.6875rem',
                                  height: '20px',
                                  fontWeight: 500,
                                  bgcolor: alpha(theme.palette.error.main, 0.08),
                                  color: theme.palette.error.main,
                                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                                  borderRadius: '4px',
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      <Box>
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Ready"
                          color="success"
                          variant="outlined"
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            display: 'block',
                            mt: 0.4,
                            fontSize: '0.725rem',
                          }}
                        >
                          All details verified
                        </Typography>
                      </Box>
                    )}
                  </TableCell>

                  {/* Action Cell */}
                  <TableCell align="center" sx={{ py: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => setEditingShipmentIndex(index)}
                      disabled={isPending}
                      sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 1.5,
                        borderColor: alpha(theme.palette.primary.main, 0.4),
                        color: theme.palette.primary.main,
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                        },
                      }}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Navigation actions */}
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 3,
          mb: 2,
          p: 2,
          borderRadius: '12px',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
          bgcolor: theme.palette.background.paper,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <SbButton variant="outlined" onClick={resetBatchMode} startIcon={<ArrowBackIcon />}>
          Switch to Single Shipment
        </SbButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {!allShipmentsValid && (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.warning.dark || '#b78103',
                fontWeight: 600,
                fontSize: '0.8125rem',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              ⚠️ Please resolve required fields in all shipments before continuing
            </Typography>
          )}
          <SbButton
            variant="contained"
            onClick={() => setActiveStep(1)}
            disabled={!allShipmentsValid}
            endIcon={<ArrowForwardIcon />}
            sx={{
              py: 1,
              px: 3,
              fontWeight: 700,
              borderRadius: '8px',
              boxShadow: allShipmentsValid ? `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
            }}
          >
            Continue to Services
          </SbButton>
        </Box>
      </Paper>

      {/* Edit Form Modal */}
      {editingShipmentIndex !== null && (
        <EditBulkShipmentModal
          shipment={bulkShipments[editingShipmentIndex]}
          onClose={() => setEditingShipmentIndex(null)}
          onSave={updated => {
            setBulkShipments((prev: BulkShipmentType[]) => {
              const copy = [...prev];
              copy[editingShipmentIndex] = updated;
              return copy;
            });
            setEditingShipmentIndex(null);
          }}
          countryList={countryList}
          currencyList={currencyList}
        />
      )}
    </Box>
  );
}
